import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, vShow as _vShow, normalizeStyle as _normalizeStyle, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "bg-white/95 dark:bg-gray-800/95 backdrop-blur-xs px-3 sm:px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap text-xs select-none z-10"
}
const _hoisted_2 = { class: "flex items-center gap-2 min-w-0" }
const _hoisted_3 = { class: "p-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex-shrink-0" }
const _hoisted_4 = { class: "truncate" }
const _hoisted_5 = { class: "font-medium text-gray-800 dark:text-gray-200 truncate block text-xs" }
const _hoisted_6 = {
  key: 0,
  class: "text-[10px] text-gray-500 dark:text-gray-400 block -mt-0.5"
}
const _hoisted_7 = { class: "flex items-center gap-1 sm:gap-1.5 ml-auto" }
const _hoisted_8 = { class: "flex items-center bg-gray-100 dark:bg-gray-700/60 rounded-xl p-0.5 border border-gray-200/80 dark:border-gray-600/60" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["disabled"]
const _hoisted_11 = ["title"]
const _hoisted_12 = {
  key: 0,
  class: "flex flex-col items-center justify-center py-20 space-y-3"
}
const _hoisted_13 = {
  key: 1,
  class: "flex flex-col items-center justify-center py-16 px-4 text-center max-w-md mx-auto space-y-3"
}
const _hoisted_14 = { class: "w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center" }
const _hoisted_15 = { class: "space-y-1" }
const _hoisted_16 = { class: "text-xs text-gray-500 dark:text-gray-400 leading-relaxed" }
const _hoisted_17 = { class: "flex items-center gap-2 pt-2" }

import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { renderAsync } from 'docx-preview'
import {
  FileText,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Download,
  AlertTriangle,
  RefreshCw,
} from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'DocxViewer',
  props: {
    src: {},
    fileName: { default: '' },
    maxHeight: { default: '600px' },
    showToolbar: { type: Boolean, default: true },
    allowDownload: { type: Boolean, default: false },
    initialZoom: { default: 100 }
  },
  emits: ["download", "rendered", "error"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const viewerRootRef = ref<HTMLElement | null>(null)
const docxContainerRef = ref<HTMLElement | null>(null)
const isLoading = ref(false)
const errorMessage = ref<string | null>(null)
const zoom = ref(props.initialZoom)
const pageCount = ref(0)
const isFullscreen = ref(false)

const zoomStyle = computed(() => {
  if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('zoom', '1')) {
    return {
      zoom: `${zoom.value}%`,
    }
  }
  const scale = zoom.value / 100
  return {
    transform: `scale(${scale})`,
    transformOrigin: 'top center',
    width: scale > 1 ? `${scale * 100}%` : '100%',
  }
})

const zoomIn = () => {
  if (zoom.value < 200) {
    zoom.value = Math.min(200, zoom.value + 15)
  }
}

const zoomOut = () => {
  if (zoom.value > 50) {
    zoom.value = Math.max(50, zoom.value - 15)
  }
}

const resetZoom = () => {
  zoom.value = 100
}

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
}

const handleDownload = () => {
  emit('download')
}

const handleKeydown = (e: KeyboardEvent) => {
  if (isFullscreen.value && e.key === 'Escape') {
    isFullscreen.value = false
  }
}

const extractData = async (): Promise<Blob | ArrayBuffer | null> => {
  if (!props.src) return null

  if (props.src instanceof Blob || props.src instanceof ArrayBuffer) {
    return props.src
  }

  if (typeof props.src === 'string') {
    const response = await fetch(props.src)
    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.statusText}`)
    }
    return await response.blob()
  }

  return null
}

const renderDocument = async () => {
  if (!props.src) {
    errorMessage.value = null
    pageCount.value = 0
    return
  }

  isLoading.value = true
  errorMessage.value = null
  pageCount.value = 0

  try {
    const data = await extractData()
    if (!data) {
      throw new Error('No document data available')
    }

    if (!docxContainerRef.value) {
      return
    }

    // Clear previous render
    docxContainerRef.value.innerHTML = ''

    await renderAsync(data, docxContainerRef.value, undefined, {
      className: 'docx',
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      breakPages: true,
      ignoreLastRenderedPageBreak: false,
      experimental: true,
      trimXmlDeclaration: true,
      renderHeaders: true,
      renderFooters: true,
      renderFootnotes: true,
      renderEndnotes: true,
      useBase64URL: true,
    })

    // Detect page count
    const pages = docxContainerRef.value.querySelectorAll('section.docx')
    pageCount.value = pages.length || 1

    emit('rendered', pageCount.value)
  } catch (err: any) {
    console.error('Docx rendering error:', err)
    const errText = err?.message || String(err)
    if (
      errText.toLowerCase().includes('zip') ||
      errText.toLowerCase().includes('end of central directory') ||
      props.fileName.toLowerCase().endsWith('.doc')
    ) {
      errorMessage.value =
        'This document appears to be in an older Word format (.doc) or a protected archive. Please download the file to view it in Microsoft Word.'
    } else {
      errorMessage.value =
        'An error occurred while parsing the Word document. Please download the file to view its full contents.'
    }
    emit('error', err)
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.src,
  () => {
    renderDocument()
  }
)

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  renderDocument()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "viewerRootRef",
    ref: viewerRootRef,
    class: _normalizeClass(["docx-viewer-component flex flex-col w-full rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 shadow-sm transition-all", { 'fixed inset-0 z-50 rounded-none h-screen max-h-screen bg-gray-900/95': isFullscreen.value }])
  }, [
    (_ctx.showToolbar)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_unref(FileText), { class: "w-4 h-4" })
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.fileName || 'Word Document'), 1),
              (pageCount.value > 0)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(pageCount.value) + " page" + _toDisplayString(pageCount.value === 1 ? '' : 's'), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("button", {
                type: "button",
                class: "p-1 rounded-lg hover:bg-white dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition",
                title: "Zoom out",
                disabled: zoom.value <= 50,
                onClick: zoomOut
              }, [
                _createVNode(_unref(ZoomOut), { class: "w-3.5 h-3.5" })
              ], 8, _hoisted_9),
              _createElementVNode("button", {
                type: "button",
                class: "px-2 py-0.5 text-[11px] font-semibold text-gray-700 dark:text-gray-200 hover:text-secondary min-w-[42px] text-center",
                title: "Reset zoom to 100%",
                onClick: resetZoom
              }, _toDisplayString(zoom.value) + "% ", 1),
              _createElementVNode("button", {
                type: "button",
                class: "p-1 rounded-lg hover:bg-white dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition",
                title: "Zoom in",
                disabled: zoom.value >= 200,
                onClick: zoomIn
              }, [
                _createVNode(_unref(ZoomIn), { class: "w-3.5 h-3.5" })
              ], 8, _hoisted_10)
            ]),
            _cache[1] || (_cache[1] = _createElementVNode("div", { class: "h-4 w-px bg-gray-200 dark:bg-gray-700 mx-0.5 hidden sm:block" }, null, -1)),
            _createElementVNode("button", {
              type: "button",
              class: "p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition",
              title: isFullscreen.value ? 'Exit Fullscreen' : 'View Fullscreen',
              onClick: toggleFullscreen
            }, [
              (isFullscreen.value)
                ? (_openBlock(), _createBlock(_unref(Minimize2), {
                    key: 0,
                    class: "w-3.5 h-3.5"
                  }))
                : (_openBlock(), _createBlock(_unref(Maximize2), {
                    key: 1,
                    class: "w-3.5 h-3.5"
                  }))
            ], 8, _hoisted_11),
            (_ctx.allowDownload)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "px-2.5 py-1 rounded-xl bg-secondary hover:bg-secondary-hover text-white font-medium text-xs flex items-center gap-1 shadow-xs transition",
                  title: "Download original document",
                  onClick: handleDownload
                }, [
                  _createVNode(_unref(Download), { class: "w-3.5 h-3.5" }),
                  _cache[0] || (_cache[0] = _createElementVNode("span", { class: "hidden sm:inline" }, "Download", -1))
                ]))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: "docx-canvas-container flex-1 overflow-auto relative p-4 sm:p-6",
      style: _normalizeStyle({ maxHeight: isFullscreen.value ? 'calc(100vh - 50px)' : _ctx.maxHeight })
    }, [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("div", { class: "w-9 h-9 border-3 border-secondary border-t-transparent rounded-full animate-spin" }, null, -1),
            _createElementVNode("p", { class: "text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300" }, " Rendering Word document preview... ", -1),
            _createElementVNode("span", { class: "text-[11px] text-gray-400" }, "Processing document structure and styles", -1)
          ]))]))
        : (errorMessage.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, [
                _createVNode(_unref(AlertTriangle), { class: "w-6 h-6" })
              ]),
              _createElementVNode("div", _hoisted_15, [
                _cache[3] || (_cache[3] = _createElementVNode("h4", { class: "text-sm font-bold text-gray-800 dark:text-gray-100" }, " Inline Preview Unavailable ", -1)),
                _createElementVNode("p", _hoisted_16, _toDisplayString(errorMessage.value), 1)
              ]),
              _createElementVNode("div", _hoisted_17, [
                _createElementVNode("button", {
                  type: "button",
                  class: "px-3.5 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 text-xs font-semibold flex items-center gap-1.5 transition",
                  onClick: renderDocument
                }, [
                  _createVNode(_unref(RefreshCw), { class: "w-3.5 h-3.5" }),
                  _cache[4] || (_cache[4] = _createElementVNode("span", null, "Try Again", -1))
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "px-3.5 py-1.5 rounded-xl bg-secondary hover:bg-secondary-hover text-white text-xs font-semibold flex items-center gap-1.5 transition shadow-xs",
                  onClick: handleDownload
                }, [
                  _createVNode(_unref(Download), { class: "w-3.5 h-3.5" }),
                  _cache[5] || (_cache[5] = _createElementVNode("span", null, "Download Document", -1))
                ])
              ])
            ]))
          : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", {
        class: "docx-zoom-wrapper w-full flex justify-center transition-transform duration-150 origin-top",
        style: _normalizeStyle(zoomStyle.value)
      }, [
        _createElementVNode("div", {
          ref_key: "docxContainerRef",
          ref: docxContainerRef,
          class: "docx-render-target w-full flex flex-col items-center"
        }, null, 512)
      ], 4), [
        [_vShow, !isLoading.value && !errorMessage.value]
      ])
    ], 4)
  ], 2))
}
}

})